<template>
    <div
        class="movie"
        :style="{ '--plyr-video-controls-background': controlsBackground }"
    >
        <video
            ref="video"
            :poster="poster"
        />
    </div>
</template>

<script>
import Plyr from 'plyr';
import Hls from 'hls.js';
import {createEasedGradient, hexToRgb} from '@/helpers/colour';
import {COLOUR_DARK} from '@/store/theme';

function getTargetTime(player, input) {
    if (typeof input === 'object' && (input.type === 'input' || input.type === 'change')) {
        return input.target.value / input.target.max * player.duration;
    } else {
        return Number(input);
    }
}

export default {
    name: 'Movie',
    props: {
        src: {type: String, default: ''},
        poster: {type: String, default: ''},
        controls: {type: Boolean, default: true},
        metadata: {type: Object, default: null},
        preventSeek: {type: [String, Boolean], default: false, options: [true, false, 'forward']}
    },
    emits: ['played'],
    data() {
        return {
            player: null,
            playerOptions: {
                loadSprite: false,
                fullscreen: {
                    enabled: true,
                    fallback: true,
                    iosNative: true
                },
                playsinline: true,
                quality: {
                    default: 1080,
                    options: [1080, 720, 360]
                },
                controls: this.controls
                    ? ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'captions', 'settings', 'pip', 'airplay', 'fullscreen']
                    : false,
                speed: {
                    selected: 1,
                    options: [0.5, 0.75, 1, 1.25, 1.5]
                },
                listeners: {seek: this.onSeek}
            },
            hls: null,
            duration: this.metadata && this.metadata.input && this.metadata.input.duration,
            source: {
                type: 'video',
                poster: this.poster,
                sources: []
            }
        };
    },
    computed: {
        controlsBackground() {
            return createEasedGradient({
                startColour: hexToRgb(COLOUR_DARK),
                endColour: [0, 0, 0, 0],
                angle: 'to top'
            });
        }
    },
    watch: {
        poster(to) {
            this.source.poster = to;
        },
        source: {
            deep: true,
            handler(to) {
                if (this.player) this.player.source = to;
            }
        }
    },
    mounted() {
        this.setSources();
    },
    methods: {
        onSeek(e) {
            if (this.preventSeek) {
                const current = this.player.currentTime;
                const target = getTargetTime(this.player, e);
                if (parseFloat(target) > current || this.preventSeek === true) {
                    e.preventDefault();
                    this.player.currentTime = current;
                    return false;
                }
            }
        },
        initPlayer() {
            this.player = new Plyr(this.$refs.video, this.playerOptions);

            // HACK: played property doesn't update when listening through Plyr
            // We also have to query the element each tick, unfortunately
            this.player.on('timeupdate', () => {
                this.$emit('played', this.$el.querySelector('video').played);
            });
            this.player.on('ended', () => {
                this.$timeout(() => this.$emit('played', this.$el.querySelector('video').played), 1000);
            });
        },
        pause() {
            if (this.player) this.player.pause();
        },
        forward(to) {
            if (this.player) this.player.forward(to);
        },
        updateQuality(newQuality) {
            if (newQuality === 0) {
                this.hls.currentLevel = -1;
            } else {
                this.hls.levels.forEach((level, levelIndex) => {
                    if (level.height === newQuality) {
                        this.hls.currentLevel = levelIndex;
                    }
                });
            }
        },
        hasSource(url) {
            return new Promise(resolve => {
                this.$http.request({ url, method: 'HEAD', withCredentials: false, headers: { Authorization: undefined } })
                    .then(() => resolve(true))
                    .catch(() => resolve(false));
            });
        },
        setSources() {
            let sizes = [1080, 720, 360];

            if (this.metadata && this.metadata.output) {
                if (Hls.isSupported() && this.metadata.output.hls) {
                    this.hls = new Hls();
                    this.hls.loadSource(this.metadata.output.hls);
                    this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
                        const availableQualities = this.hls.levels.map((l) => l.height).reverse();
                        availableQualities.unshift(0);

                        this.playerOptions.quality = {
                            default: 0, // Auto
                            options: availableQualities,
                            forced: true,
                            onChange: this.updateQuality,
                        };
                        this.playerOptions.i18n = { qualityLabel: { 0: 'Auto' } };

                        this.hls.on(Hls.Events.LEVEL_SWITCHED, () => {
                            const span = document.querySelector(".plyr__menu__container [data-plyr='quality'][value='0'] span");
                            span.innerHTML = `Auto`;
                        });
                        this.hls.attachMedia(this.$refs.video);
                        this.initPlayer();
                    });
                } else {
                    sizes = Object.keys(this.metadata.output.mp4).filter(size => !!this.metadata.output.mp4[size]);
                    const sources = sizes.map(size => ({
                        src: this.metadata.output.mp4[size],
                        type: 'video/mp4',
                        size: size
                    }));
                    this.source.sources = sources;
                    this.initPlayer();
                }
            } else {
                const urls = sizes.map(size => `${this.src}mp4-${size}.mp4`);
                Promise.all(urls.map(this.hasSource)).then(hasSources => {
                    let sources = [];

                    hasSources.forEach((hasSource, index) => {
                        if (hasSource) {
                            sources.push({
                                src: urls[index],
                                type: 'video/mp4',
                                size: sizes[index]
                            });
                        }
                    });

                    this.source.sources = sources;
                });
                this.initPlayer();
            }
        }
    }
};
</script>

<style lang="less">
@import 'plyr/dist/plyr.css';

.movie {
    width: 100%;
    min-width: auto;
    height: 100%;
    --movie-colour-internal: var(--movie-colour, var(--primary));
    --movie-colour-tint-1-internal: var(--movie-colour-tint-1, var(--primary-tint-1));
    --plyr-color-main: #FFF;
    --plyr-video-background: var(--coal);
    --plyr-video-control-color: #FFF;
    --plyr-video-control-color-hover: #FFF;
    --plyr-video-control-background-hover: transparent;
    --plyr-control-icon-size: 20px;
    --plyr-control-radius: 8px;
    --plyr-control-spacing: 8px;
    --plyr-control-padding: ~'calc(var(--plyr-control-spacing) * 0.5)';
    --plyr-font-family: 'Roboto', sans-serif;
    --plyr-menu-background: var(--coal);
    --plyr-menu-color: #FFF;
    --plyr-menu-item-arrow-color: rgba(255, 255, 255, 0.12);
    --plyr-menu-arrow-color: #FFF;
    --plyr-menu-border-color: rgba(255, 255, 255, 0.12);
    --plyr-menu-back-border-color: rgba(255, 255, 255, 0.12);
    --plyr-menu-back-border-shadow-color: transparent;
    --plyr-menu-shadow: none;
    --plyr-menu-radius: 12px;

    .plyr {
        width: 100%;
        min-width: auto;
        height: 100%;
    }

    .plyr__controls {
        .plyr__controls__item {
            padding: 4px;
            color: rgba(255, 255, 255, 0.75);

            > .plyr__control {
                padding: 0;
            }

            // Play button (controls)
            &:first-child {
                padding: 6px;
                color: #FFF;
                border-radius: 1000px;
                margin-right: 4px;

                &:hover {
                    background: rgba(255, 255, 255, 0.4);
                }
            }

            &.plyr__time {
                white-space: nowrap;
                color: #FFF;
            }
        }
    }

    .plyr--video {
        .plyr__control--overlaid {
            padding: 20px;
            border-radius: 1000px;
            background-color: var(--movie-colour-fade-1-internal);

            &:hover {
                background-color: var(--movie-colour-internal);
            }

            svg {
                width: 40px;
                height: 40px;
            }
        }

        .plyr__control {
            &:hover {
                color: #FFF;
            }
        }

        .plyr__controls {
            padding: 12px;
            padding-top: 32px;
        }
    }

    .plyr--full-ui {
        input[type=range] {
            transition: color 0.15s var(--curve);

            &:hover {
                --plyr-color-main: var(--movie-colour-internal);
            }
        }
    }

    .plyr__menu__container {
        border-radius: 12px;

        .plyr__control {
            &:hover {
                background-color: rgba(255, 255, 255, 0.4);
            }

            &[role=menuitemradio] {
                &::before {
                    background-color: rgba(255, 255, 255, 0.1);
                }

                &::after {
                    left: 11px;
                }
            }
        }
    }
}
</style>
